import { useCallback, useMemo, useState } from 'react';
import {
  BuyXPayY,
  PaymentOptionInfoDto,
  ProgressiveDiscount,
  PromotionRequestDto,
  Reward,
} from '@tiendanube/common';
import { LimitScopeTypes } from '@tiendanube/common/src/domains/marketing/promotions/LimitScopeTypes';
import { PromotionTypes } from '@tiendanube/common/src/domains/marketing/promotions/PromotionTypes';
import { useNavegate } from 'App/hooks';
import { toStatusType } from 'App/rtk';
import { ErrorScreen } from 'commons/components';
import { useForm, useToastStatus } from 'commons/hooks';
import { isEnumValue } from 'commons/utils';
import { logMoreDiscountsModalOptionCreated } from 'domains/Marketing/components/MoreDiscounts';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import {
  DATE_TYPE_QUERY_PARAM,
  DISCOUNT_OPTION_ID_QUERY_PARAM,
  MARKETING_ROUTES,
} from 'domains/Marketing/marketingRoutes';
import {
  promotionCrossSellingDefault,
  promotionDefault,
  promotionPercentageOffDefault,
  promotionProgressiveDiscountDefault,
} from './constants';
import { ScopeValuesInterface } from './types';
import { validationSchema } from './validationSchema';
import {
  PromotionPage,
  PromotionsDetailsSkeletons,
  PromotionForm,
} from '../components';
import {
  useCreatePromotionMutation,
  usePromotionTypesQuery,
} from '../promotionsApi';
import { PROMOTION_TYPE_QUERY_PARAM } from '../utils';

export function NewPromotionPage() {
  const t = useTranslationMarketing('promotions.settingsPage.add');

  const { goTo, getQueryParam } = useNavegate();

  const [isExternal, setIsExternal] = useState(false);

  const [createPromotion, { status: statusCreate }] =
    useCreatePromotionMutation();

  const { status: statusType, refetch: refecchTypes } =
    usePromotionTypesQuery();

  const discountOptionId = getQueryParam(DISCOUNT_OPTION_ID_QUERY_PARAM);

  const handleCreate = (data: PromotionRequestDto) => {
    createPromotion(data);
    if (discountOptionId !== null) {
      logMoreDiscountsModalOptionCreated(discountOptionId);
    }
  };

  const promotionTypeQueryParam = getQueryParam(PROMOTION_TYPE_QUERY_PARAM);
  const dateTypeQueryParam = getQueryParam(DATE_TYPE_QUERY_PARAM);

  const initialValues = useMemo(() => {
    const baseValuesMap = {
      [PromotionTypes.CROSS_SELLING]: promotionCrossSellingDefault,
      [PromotionTypes.PROGRESSIVE_DISCOUNT]:
        promotionProgressiveDiscountDefault,
      [PromotionTypes.PERCENTAGE_OFF]: promotionPercentageOffDefault,
    };

    const baseValues =
      (promotionTypeQueryParam && baseValuesMap[promotionTypeQueryParam]) ??
      promotionDefault;

    return {
      ...baseValues,
      ...(isEnumValue(dateTypeQueryParam, LimitScopeTypes) && {
        dateType: dateTypeQueryParam,
      }),
    };
  }, [promotionTypeQueryParam, dateTypeQueryParam]);

  const {
    values,
    errors,
    isDirty,
    handleChange,
    setFieldValue,
    handleChangeSelect,
    handleOnSubmit,
  } = useForm({
    initialValues,
    validationSchema,
    onSubmit: handleCreate,
  });

  const handleChangeCategories = (scopeValues: ScopeValuesInterface[]) => {
    setFieldValue('categories', scopeValues);
  };

  const handleChangeProducts = (scopeValues: ScopeValuesInterface[]) => {
    setFieldValue('products', scopeValues);
  };

  const handleChangeProgressiveDiscounts = (
    progressiveDiscounts: ProgressiveDiscount[],
  ) => {
    setFieldValue('progressiveDiscounts', progressiveDiscounts);
  };

  const handleChangeBuyXPayY = (buyXPayY: BuyXPayY | null) => {
    setFieldValue('buyXPayY', buyXPayY);
  };

  const handleChangeRewards = (rewards: Reward[]) => {
    setFieldValue('rewards', rewards);
  };

  const handleChangePercentageOff = (percentageOff: number) => {
    setFieldValue('percentageOffDiscount', percentageOff);
  };

  const handleChangePaymentOptions = (
    paymentOptions: PaymentOptionInfoDto[],
  ) => {
    setFieldValue('paymentOptions', paymentOptions);
  };

  const handleChangeIsExternal = useCallback((value: boolean) => {
    setIsExternal(value);
  }, []);

  const saveStatus = toStatusType(statusCreate);
  const loadStatus = toStatusType(statusType);

  useToastStatus({
    error: t('error'),
    success: t('success'),
    status: saveStatus,
    onSuccess: () => goTo(MARKETING_ROUTES.promotionsList),
  });

  return (
    <PromotionPage
      mode="add"
      isDirty={isDirty}
      isExternal={isExternal}
      saveStatus={saveStatus}
      loadStatus={loadStatus}
      onSave={handleOnSubmit}
    >
      {loadStatus === 'error' && <ErrorScreen onRetry={refecchTypes} />}
      {loadStatus === 'loading' && <PromotionsDetailsSkeletons />}
      {loadStatus === 'success' && (
        <PromotionForm
          mode="add"
          values={values}
          errors={errors}
          isExternal={isExternal}
          onChange={handleChange}
          setFieldValue={setFieldValue}
          onChangeSelect={handleChangeSelect}
          onChangeCategories={handleChangeCategories}
          onChangeProducts={handleChangeProducts}
          onChangeIsExternal={handleChangeIsExternal}
          onChangeProgressiveDiscounts={handleChangeProgressiveDiscounts}
          onChangeBuyXPayY={handleChangeBuyXPayY}
          onChangeRewards={handleChangeRewards}
          onChangePercentageOff={handleChangePercentageOff}
          onChangePaymentOptions={handleChangePaymentOptions}
        />
      )}
    </PromotionPage>
  );
}
